"use client";

import PushNotification from "@/components/common/PushNotification";
import { env } from "@/env/index.mjs";
import { getAnalyticsDebug } from "@/hooks/use-analytics-debug";
import { ClerkProvider } from "@clerk/nextjs";
import { experimental_createTheme } from "@clerk/themes";
import { GoogleAnalytics } from "@next/third-parties/google";
import { ThemeProvider, useTheme } from "next-themes";
import { usePathname } from "next/navigation";

function isOfficeTaskpanePath(pathname: string | null): boolean {
    return (
        pathname === "/office/taskpane" ||
        pathname === "/office/taskpane-probe" ||
        (pathname?.startsWith("/office/taskpane/") ?? false)
    );
}

export function ClientRoot({ children }: { children: React.ReactNode }) {
    const isDebug = getAnalyticsDebug();
    const pathname = usePathname();

    return (
        <>
            <ThemeProvider
                enableSystem
                attribute="class"
                storageKey="theme"
                defaultTheme="system"
                // The Word taskpane is a light-only UI (hard-coded white
                // surfaces). Under a dark system theme the base-layer heading
                // colour flips to near-white and renders as a faded "ghost"
                // title on the white pane, so pin it to light.
                forcedTheme={isOfficeTaskpanePath(pathname) ? "light" : undefined}
                disableTransitionOnChange
            >
                <ClerkWrapped isDebug={isDebug}>{children}</ClerkWrapped>
            </ThemeProvider>
            {isDebug !== undefined && <GoogleAnalytics gaId="G-E97MMMXJZV" debugMode={isDebug} />}
        </>
    );
}

const clerkCustomDarkTheme = experimental_createTheme({
    variables: {
        colorBackground: "#020817",
        colorNeutral: "white",
        colorPrimary: "#2564eb",
        colorTextSecondary: "#9CA3AF",
        colorTextOnPrimaryBackground: "#f8fafc",
        colorText: "white",
        colorInputText: "black",
        colorInputBackground: "#26262B",
    },
    elements: {
        providerIcon__apple: { filter: "invert(1)" },
        providerIcon__github: { filter: "invert(1)" },
        activeDeviceIcon: {
            "--cl-chassis-bottom": "#d2d2d2",
            "--cl-chassis-back": "#e6e6e6",
            "--cl-chassis-screen": "#e6e6e6",
            "--cl-screen": "#111111",
        },
    },
});

function ClerkWrapped({ children, isDebug }: { children: React.ReactNode; isDebug: boolean | undefined }) {
    const { resolvedTheme } = useTheme();
    const pathname = usePathname();

    // Skip Clerk for the production Office taskpane (runs in iframe with cookie restrictions).
    // The auth spike page (/office/taskpane-spike) keeps Clerk so it can exercise the inline session flow.
    const isTaskpane = isOfficeTaskpanePath(pathname);
    // The Microsoft sign-in popup lands here before any Clerk session exists;
    // it renders no Clerk UI, so skip the provider to keep this critical-path
    // page minimal.
    const isOfficeAuthRedirect = pathname === "/office/auth-redirect";

    // Don't render Clerk components until analytics debug is resolved
    if (isDebug === undefined) {
        return <div />;
    }

    // Skip ClerkProvider for taskpane to avoid auth issues in iframe
    if (isTaskpane || isOfficeAuthRedirect) {
        return <main>{children}</main>;
    }

    return (
        <ClerkProvider
            publishableKey={env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY}
            appearance={{
                baseTheme: resolvedTheme === "dark" ? clerkCustomDarkTheme : undefined,
            }}
            allowedRedirectOrigins={[
                "http://localhost:3001",
                "http://localhost:3002", // landing dev server
                "https://ratemynda.com",
                "https://www.ratemynda.com",
                "https://contractrabbit.com", // apex (marketing landing)
                "https://app.contractrabbit.com",
                "https://www.contractrabbit.com",
                /^https:\/\/[a-z0-9-]+\.ngrok-free\.(app|dev)$/, // Allow ngrok domains
            ]}
        >
            <PushNotification />
            <main>{children}</main>
        </ClerkProvider>
    );
}
